import { Box } from '@mui/material';
import { Editor } from '../../../../../components/wysiwyg/Editor';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from '../../../../../store/snackbar';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';
import { z } from 'zod';

const activityGoalsAndObjectivesQuery = graphql(`
	query journeyDetail_getActivityGoalsAndObjectives($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			goalsAndObjectives
		}
	}
`);

const updateGoalsAndObjectivesMutation = graphql(`
	mutation journeyDetail_updateGoalsAndObjectives($id: UUID!, $goalsAndObjectives: String) {
		updateActivity(by: { id: $id }, data: { goalsAndObjectives: $goalsAndObjectives }) {
			ok
			errorMessage
		}
	}
`);

export const goalsAndObjectivesEditDrawerSchema = z.object({
	activityId: z.string(),
});

export const GoalsAndObjectivesEditDrawer: FC<z.infer<typeof goalsAndObjectivesEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const [goalsAndObjectives, setGoalsAndObjectives] = useState('');
	const [isEditorReady, setIsEditorReady] = useState(false);
	const { openSnackbar } = useSnackbar();

	const { data, isPending } = useApiQuery(activityGoalsAndObjectivesQuery, {
		variables: { activityId },
	});

	const { mutate: updateActivitGoalsAndObjectives } = useApiMutation(updateGoalsAndObjectivesMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'widget.goals-and-objectives.update.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			}),
	});

	useEffect(() => {
		if (!isPending) {
			setGoalsAndObjectives(data?.getActivity?.goalsAndObjectives ?? '');
			setIsEditorReady(true);
		}
		// excluding other dependencies as latest data are passed in when isPending changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPending]);

	useDebounceEffect(() => {
		if (isEditorReady && data?.getActivity?.goalsAndObjectives !== goalsAndObjectives) {
			updateActivitGoalsAndObjectives({ id: activityId, goalsAndObjectives: goalsAndObjectives });
		}
	}, [goalsAndObjectives, data, isEditorReady]);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.goals-and-objectives.title' })} />
			<Box width={'100%'}>
				{isEditorReady && <Editor placeholder={'...'} state={goalsAndObjectives} setState={setGoalsAndObjectives} />}
			</Box>
		</DrawerWrapper>
	);
};
